import { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import HomePage from './pages/HomePage';
import LandingPage from './pages/Auth/LandingPage';
import SsoCallback from './pages/Auth/SsoCallback';
import PaymentPage from './pages/PaymentPage';
import AnalyticsDashboard from './pages/Analytics/AnalyticsDashboard';
import LeaderboardPage from './pages/Leaderboard/LeaderboardPage';
import './App.css';

type AgeGroupType = '6-9' | '10-13' | '14+';

// Protected Route Component - Redirects to /auth if not authenticated
const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4 animate-bounce">📚</div>
          <h1 className="text-4xl font-bold text-indigo-800 mb-2">Knoozy</h1>
          <p className="text-indigo-600">Loading your personalized stories...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  return children;
};

// Public Route Component - Redirects to /dashboard if already authenticated
const PublicRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4 animate-bounce">📚</div>
          <h1 className="text-4xl font-bold text-indigo-800 mb-2">Knoozy</h1>
          <p className="text-indigo-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Main App Content
const AppContent: React.FC = () => {
  const { user } = useAuth();
  const [demoAgeGroup, setDemoAgeGroup] = useState<AgeGroupType>('10-13');

  // "Staff" = admins and teachers. Both get the age-group toggle, full article
  // access (no paywall), and the Analytics dashboard. Only admins additionally
  // hold user-management powers (enforced server-side, not here).
  const isAdmin = user?.role === 'admin' || user?.role === 'teacher';

  // Use user's age group, or demo age group for staff only
  const currentAgeGroup = isAdmin
    ? demoAgeGroup
    : (user?.ageGroup as AgeGroupType) || '10-13';
  const currentStudentName = user?.studentName || 'Student';

  // Age group selector for ADMIN ONLY
  const AgeGroupSelector = () => (
    <div className="fixed bottom-4 left-4 z-50 bg-white rounded-lg shadow-lg p-4 border-2 border-gray-200">
      <p className="text-sm font-semibold mb-2">Admin: Select Age Group</p>
      <div className="flex space-x-2">
        <button
          onClick={() => setDemoAgeGroup('6-9')}
          className={`px-3 py-1 rounded text-sm ${
            currentAgeGroup === '6-9'
              ? 'bg-yellow-400 text-white'
              : 'bg-gray-200 text-gray-700'
          }`}
        >
          6-9
        </button>
        <button
          onClick={() => setDemoAgeGroup('10-13')}
          className={`px-3 py-1 rounded text-sm ${
            currentAgeGroup === '10-13'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-200 text-gray-700'
          }`}
        >
          10-13
        </button>
        <button
          onClick={() => setDemoAgeGroup('14+')}
          className={`px-3 py-1 rounded text-sm ${
            currentAgeGroup === '14+'
              ? 'bg-indigo-600 text-white'
              : 'bg-gray-200 text-gray-700'
          }`}
        >
          14+
        </button>
      </div>
    </div>
  );

  return (
    <div className="App">
      <Routes>
        {/* Public Route - Redirects to /dashboard if already logged in */}
        <Route
          path="/auth"
          element={
            <PublicRoute>
              <LandingPage />
            </PublicRoute>
          }
        />

        {/* Protected Dashboard Route */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <>
                <HomePage ageGroup={currentAgeGroup} studentName={currentStudentName} isAdmin={isAdmin} />
                {isAdmin && <AgeGroupSelector />}
              </>
            </ProtectedRoute>
          }
        />

        {/* Protected Payment Route */}
        <Route
          path="/payment"
          element={
            <ProtectedRoute>
              <PaymentPage />
            </ProtectedRoute>
          }
        />

        {/* Staff-only Analytics dashboard (admins + teachers). Non-staff are
            bounced back to the dashboard. */}
        <Route
          path="/analytics"
          element={
            <ProtectedRoute>
              {isAdmin ? <AnalyticsDashboard /> : <Navigate to="/dashboard" replace />}
            </ProtectedRoute>
          }
        />

        {/* Class leaderboard (students see their own class rank) */}
        <Route
          path="/leaderboard"
          element={
            <ProtectedRoute>
              <LeaderboardPage />
            </ProtectedRoute>
          }
        />

        {/* School SSO landing (e.g. SSM parent portal) - public */}
        <Route path="/sso/callback" element={<SsoCallback />} />

        {/* Redirect root to dashboard */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* Catch all - redirect to dashboard */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </div>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <SubscriptionProvider>
          <AppContent />
        </SubscriptionProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
